import React from 'react';
import { formatCurrency } from '../../utils/helpers';
import { calculations } from '../NewAccount/SavingsCalculator/calculations';

export const formatRateValue = (rate) => {
    if (rate === null || rate === undefined || rate === '') return 'x.xx%';
    const numericRate = Number(rate);
    return Number.isFinite(numericRate) ? `${numericRate.toFixed(2)}%` : 'x.xx%';
};

const formatProductRate = (product) => formatRateValue(product?.rate);

export const formatBalance = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return '';
    }

    const amount = Number(value);
    const fractionDigits = Number.isInteger(amount) ? 0 : 2;
    return `£${amount.toLocaleString('en-GB', {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: 2,
    })}`;
};

export const formatTierRange = (tier) => {
    const min = formatBalance(tier?.minBalance);
    const max = formatBalance(tier?.maxBalance);

    if (min && max) return `${min} - ${max}`;
    if (min) return `${min}+`;
    if (max) return `Up to ${max}`;
    return 'All balances';
};

const getRateTiers = (product) => (product?.interestRateTiers || [])
    .filter(tier => tier && tier.rate !== null && tier.rate !== undefined)
    .slice()
    .sort((left, right) => Number(left.minBalance || 0) - Number(right.minBalance || 0));

const getPrimaryRateTier = (tiers) => tiers.find(tier => Number(tier.minBalance || 0) > 0) || tiers[tiers.length - 1];

const formatTierRateDisplay = (tier) => {
    const grossRate = formatRateValue(tier.rate);
    if (Number(tier.rate) === 0 || tier.aerRate === undefined || tier.aerRate === null) {
        return grossRate;
    }

    return `${formatRateValue(tier.aerRate)} / ${grossRate}`;
};

const calculateExampleProjection = (product) => calculations.saveRegularly( 0, 1000, 12, Number(product?.rate || 0), false );

const renderInterestRateBody = (product) => {
    const tiers = getRateTiers(product);
    const primaryTier = getPrimaryRateTier(tiers);
    const primaryMinimum = formatBalance(primaryTier?.minBalance);
    const primaryRange = primaryTier ? formatTierRange(primaryTier) : '';
    const primaryRate = primaryTier ? formatRateValue(primaryTier.rate) : formatProductRate(product);

    return (
        <div>
            <div className="summary-box-table table-border disa-rate-table">
                <div className="heading">Balance</div>
                <div className="heading">AER* / Gross p.a. (variable)</div>
                {tiers.map((tier, index) => (
                    <React.Fragment key={`${tier.minBalance ?? 'min'}-${tier.maxBalance ?? 'max'}-${index}`}>
                        <div className="content">{formatTierRange(tier)}</div>
                        <div className="content">{formatTierRateDisplay(tier)}</div>
                    </React.Fragment>
                ))}
            </div>
            <p>Interest is calculated daily and paid into your Instant Access Cash ISA on the 21st day of each month.</p>
            {primaryTier ? (
                    <p>
                        You&apos;ll earn {primaryRate} gross p.a.^ on your entire balance when it&apos;s between {primaryRange}.
                        {primaryMinimum && <p>{`However, if your balance is below ${primaryMinimum} you won't earn any interest.`}</p>}
                    </p>
            ) : (
                <p>You&apos;ll earn {primaryRate} gross p.a.^. Balance tiers are currently unavailable.</p>
            )}
        </div>
    );
};

export const getMaturitySteps = (enableUpdatedMaturityCopy = false) => [
    {
        title: 'Your Fixed Rate Cash ISA reaches maturity',
        body: 'Your fixed term ends and your savings are ready to be moved into your new Instant Access Cash ISA. You do not have to do anything, we will do this automatically.',
    },
    {
        title: 'Your new Instant Access Cash ISA is open',
        body: enableUpdatedMaturityCopy
            ? 'We\'ll move your savings including any interest earned into an Instant Access Cash ISA.'
            : 'We move your savings into your Instant Access Cash ISA and pay any interest earned to your linked account.',
    },
    {
        title: 'Your savings are now ready for you to manage',
        body: 'Log in to your dashboard to manage your savings.',
    },
];

export const maturityBenefits = [
    'Your savings will continue earning tax-free interest.',
    'You can transfer your savings into a new Fixed Rate Cash ISA.',
    'You can transfer an existing cash ISA into this account or transfer your ISA to another provider.',
    'You can top up your balance, subject to your available annual allowance.',
    'You can withdraw money when you need it, although it will lose its tax-free status.',
    'You can close your account at any time through your online dashboard.',
];

export const summarySections = [
    {
        title: 'Account name?',
        body: <p>Instant Access Cash ISA.</p>,
    },
    {
        title: 'What is the interest rate?',
        body: renderInterestRateBody(),
    },
    {
        title: 'Can Investec Save change the interest rate?',
        body: (
            <p>
                Yes, the interest rate is variable. We&apos;ll always give you at least 14 days advance notice if we decrease it. For more details on how the rate can change and how we&apos;ll let you know, please see our Product Terms.
            </p>
        ),
    },
    {
        title: 'What would the estimated balance be after 12 months based on a \u00a31,000 deposit?',
        body: (
            <div>
                <p>
                    At current interest rates, a deposit of £1,000 would generate interest of £[XXX.XX] after 12 months. This would provide a final balance of £[XXX.XX].
                </p>
                <p>This example is illustrative only and is not based on individual circumstances. It also assumes:</p>
                <ul>
                    <li>No withdrawals or further deposits are made.</li>
                    <li>Interest is paid into the Instant Access Cash ISA.</li>
                    <li>There's no change to the interest rate.</li>
                </ul>
            </div>
        ),
    },
    {
        title: 'How do I open and manage my account?',
        body: (
            <div>
                <p>
                    When your Fixed Rate Cash ISA reaches maturity it will automatically convert into an Instant Access Cash ISA. We&apos;ll let you know when your Instant Access Cash ISA account is ready to use. You can then manage your account online through your Investec Save dashboard.
                </p>
                <p>You can:</p>
                <ul>
                    <li>Transfer in an existing cash ISA from another provider &ndash; please check our FAQs for our current list of accepted providers before initiating a transfer</li>
                    <li>Transfer your Investec Save Cash ISA to another provider.</li>
                    <li>Fund a new Fixed Rate Cash ISA.</li>
                    <li>Top up your account, subject to your remaining annual ISA allowance.</li>
                    <li>Withdraw money to your linked account</li>
                    <li>Move your money to another Investec Save account.</li>
                    <li>Close your account at any time with no penalty.</li>
                </ul>
                <p>Transfers between ISAs will not impact your savings&apos; tax-free status.</p>
                <p><strong>Please note: you cannot exceed your annual ISA allowance in any tax year.</strong></p>
            </div>
        ),
    },
    {
        title: 'Can I withdraw money?',
        body: (
            <div>
                <p>
                    Yes. You can withdraw all or some of your money whenever you need to. Withdrawals can be made to your linked account or, if available, to another Investec Save account. If you withdraw money from your ISA, it will no longer be tax free. Interest already earned within the ISA up to the date of withdrawal retains its tax-free status.
                </p>
                <p>
                    Because this is not a flexible Cash ISA, if you repay that withdrawn money later,
                    it will count towards your remaining annual ISA allowance.
                </p>
            </div>
        ),
    },
    {
        title: 'Additional information',
        body: (
            <div>
                <p>
                    ISA stands for Individual Savings Account. It is a savings or investment account that offers tax benefits.
                    Interest will be paid without tax being deducted.
                </p>
                <p>
                    Normally, interest earned on savings accounts is taxable if it exceeds your personal savings allowance (PSA). But with an ISA, the interest you earn doesn't count towards your PSA, so you won't pay income tax on any returns your ISA makes.
                </p>
                <p>Tax treatment depends on your individual circumstances and may be subject to change in the future.</p>
                <p>Terms and conditions apply. Further guidance can be found on the HMRC website.</p>
            </div>
        ),
    },
];

export const getSummarySections = (product) => summarySections.map((section) => {
    if (section.title === 'What is the interest rate?') {
        return {
            ...section,
            body: renderInterestRateBody(product),
        };
    }

    if (section.title === 'What would the estimated balance be after 12 months based on a \u00a31,000 deposit?') {
        const projection = calculateExampleProjection(product);

        return {
            ...section,
            body: (
                <div>
                    <p>
                        At current interest rates, a deposit of {formatCurrency(1000, false)} would generate interest of {formatCurrency(projection.interest)} after 12 months. This would provide a final balance of {formatCurrency(projection.saved)}.
                    </p>
                    <p>This example is illustrative only and is not based on individual circumstances. It also assumes:</p>
                    <ul>
                        <li>No withdrawals or further deposits are made.</li>
                        <li>Interest is paid into the Instant Access Cash ISA.</li>
                        <li>There's no change to the interest rate.</li>
                    </ul>
                    <p><strong>Please note that if your account balance falls below £1,000, you will earn 0.00% gross p.a. (variable)/AER*.</strong></p>
                </div>
            ),
        };
    }

    return section;
});

export const faqs = [
    {
        title: 'Can I transfer a cash ISA from another provider to Investec Save?',
        body: 'Yes. Once your Instant Access Cash ISA is open, you\'ll be able to transfer existing cash ISAs from other providers, subject to ISA rules.',
    },
    {
        title: 'Can I transfer this Instant Access Cash ISA into another Fixed Rate Cash ISA?',
        body: 'Yes. You’ll need to apply for a new Fixed Rate Cash ISA first. Once it’s open, you can move your savings from your online dashboard.',
    },
    {
        title: 'Can I transfer this Investec Save Instant Access Cash ISA to another provider?',
        body: 'Yes. You can transfer your full ISA balance to another provider at any time. Your new provider will arrange the transfer on your behalf. Your Instant Access Cash ISA will close once the transfer is complete.',
    },
];
